import React, { useMemo } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "../../Icons";

const PaginationButton = ({
  onClick,
  disabled,
  active,
  children,
  className = "",
}) => {
  const baseClasses =
    "w-8 h-8 flex items-center justify-center rounded-md text-xs font-medium transition-colors";
  const activeClasses =
    "bg-indigo-50 text-indigo-700 border border-indigo-200 font-semibold z-10";
  const inactiveClasses =
    "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900";
  const disabledClasses =
    "opacity-50 cursor-not-allowed bg-slate-50 text-slate-400 border border-slate-200";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${
        disabled ? disabledClasses : active ? activeClasses : inactiveClasses
      } ${className}`}
    >
      {children}
    </button>
  );
};

const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  totalItems = 0,
  itemsPerPage = 20,
}) => {
  const paginationRange = useMemo(() => {
    if (totalPages <= 1) return [];

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }

    if (currentPage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  }, [totalPages, currentPage]);

  if (totalPages <= 1 && totalItems <= itemsPerPage) return null;

  const start = Math.min((currentPage - 1) * itemsPerPage + 1, totalItems);
  const end = Math.min(currentPage * itemsPerPage, totalItems);

  const handleNext = () => {
    onPageChange(Math.min(currentPage + 1, totalPages));
  };

  const handlePrevious = () => {
    onPageChange(Math.max(currentPage - 1, 1));
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between px-3 py-2.5 border-t border-slate-100 bg-white gap-3 w-full">
      <div className="text-[12px] text-slate-500 font-medium">
        Showing{" "}
        <span className="text-slate-800 font-semibold">{start || 0}</span> to{" "}
        <span className="text-slate-800 font-semibold">{end || 0}</span> of{" "}
        <span className="text-slate-800 font-semibold">{totalItems || 0}</span>{" "}
        entries
      </div>

      <div className="flex items-center gap-1.5">
        <PaginationButton onClick={handlePrevious} disabled={currentPage === 1}>
          <ChevronLeftIcon size={16} />
        </PaginationButton>

        <div className="hidden sm:flex items-center gap-1.5">
          {paginationRange.map((pageNumber, index) => {
            if (pageNumber === "...") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 font-medium select-none text-xs"
                >
                  &#8230;
                </span>
              );
            }

            return (
              <PaginationButton
                key={`page-${pageNumber}`}
                active={pageNumber === currentPage}
                onClick={() => onPageChange(pageNumber)}
              >
                {pageNumber}
              </PaginationButton>
            );
          })}
        </div>

        <div className="flex sm:hidden items-center text-[12px] text-slate-600 font-medium mx-1.5">
          Page {currentPage} of {totalPages}
        </div>

        <PaginationButton
          onClick={handleNext}
          disabled={currentPage === totalPages}
        >
          <ChevronRightIcon size={16} />
        </PaginationButton>
      </div>
    </div>
  );
};

export default Pagination;
